import { parsePhoneNumberWithError } from 'libphonenumber-js/min';
import { z } from 'zod';
/**
 * Нормализация телефона в E.164.
 *
 * Это самое скучное место в проекте и одно из самых важных. Телефон —
 * единственный ключ, по которому связываются контакт из заявки, чат в
 * WhatsApp и заказ в CRM. Одна и та же строка приходит в десятке написаний:
 * `+52 1 55 1234 5678`, `5215512345678`, `0055 11 91234-5678`,
 * `(11) 91234-5678`. Если хранить как пришло, один человек превратится в
 * трёх контактов, диалог начнётся заново, а конверсия уйдёт не на тот клик.
 * Поэтому в базе лежит ровно один вид — E.164, `+` и цифры.
 *
 * Разбор делает libphonenumber-js, а не наша регулярка, и причина не в
 * лени. Правила номеров национальные и меняются: в Мексике до 2019 года
 * мобильные писались с «1» после кода страны, в Бразилии девятая цифра
 * добавлялась по регионам постепенно, в Аргентине мобильный внутри страны
 * набирается с «15», а из-за границы — с «9» после кода. Ни одно из этих
 * правил не выводится из формы строки, только из таблиц. Берётся сборка
 * `/min` — она знает форматы и коды стран, но не различает типы номеров;
 * для нашей задачи этого достаточно, а метаданные вчетверо меньше.
 */
export const E164_PATTERN = /^\+[1-9]\d{6,18}$/;
export class PhoneParseError extends Error {
    reason;
    constructor(reason, message) {
        super(message);
        this.name = 'PhoneParseError';
        this.reason = reason;
    }
}
/**
 * Приводит телефон к E.164 или объясняет, почему не получилось.
 *
 * `defaultCountry` нужен для номеров без кода страны: из заявки часто
 * приходит местная запись вида `11 91234-5678`, и превратить её в
 * международную без знания страны нельзя. Страна берётся из настроек
 * кампании или канала — то есть из контекста, в котором номер появился,
 * а не угадывается.
 */
export function normalizePhone(input, defaultCountry) {
    const raw = input.trim();
    if (raw.length === 0) {
        throw new PhoneParseError('empty', 'Телефон не указан');
    }
    try {
        const parsed = parsePhoneNumberWithError(raw, defaultCountry ? defaultCountry.toUpperCase() : undefined);
        if (!parsed.isValid()) {
            throw new PhoneParseError('invalid', `«${raw}» не похож на существующий номер${defaultCountry ? ` в стране ${defaultCountry.toUpperCase()}` : ''}`);
        }
        return { e164: parsed.number, country: parsed.country ?? null };
    }
    catch (error) {
        if (error instanceof PhoneParseError)
            throw error;
        /**
         * Библиотека бросает свои ошибки с кодами вида NOT_A_NUMBER или
         * INVALID_COUNTRY. Превращаем их в понятный текст: сообщение уйдёт
         * оператору, который загружает список контактов, и «TOO_SHORT» ему
         * ничего не скажет.
         */
        const code = error instanceof Error ? error.message : String(error);
        throw new PhoneParseError(code, describeParseError(code, raw));
    }
}
function describeParseError(code, raw) {
    switch (code) {
        case 'NOT_A_NUMBER':
            return `«${raw}» не похож на телефон`;
        case 'TOO_SHORT':
            return `«${raw}» слишком короткий для телефона`;
        case 'TOO_LONG':
            return `«${raw}» слишком длинный для телефона`;
        case 'INVALID_COUNTRY':
            return `Не удалось определить страну номера «${raw}». Укажите его в международном виде, с кодом страны`;
        default:
            return `Не удалось разобрать телефон «${raw}»`;
    }
}
/** Мягкий вариант: возвращает null вместо исключения. */
export function tryNormalizePhone(input, defaultCountry) {
    try {
        return normalizePhone(input, defaultCountry);
    }
    catch {
        return null;
    }
}
/** Схема для полей, куда телефон уже должен приходить нормализованным. */
export const e164Schema = z
    .string()
    .regex(E164_PATTERN, 'Ожидается телефон в виде +код и цифры, например +5215512345678');
/**
 * Показ телефона человеку.
 *
 * В интерфейсе номер показывается в международном виде с пробелами:
 * `+52 155 1234 5678` читается и сверяется глазами заметно легче, чем
 * слитная строка. В базе при этом по-прежнему лежит E.164 — форматирование
 * это функция показа, а не хранения.
 */
export function formatPhone(e164) {
    const parsed = tryNormalizePhone(e164);
    if (!parsed)
        return e164;
    try {
        return parsePhoneNumberWithError(e164).formatInternational();
    }
    catch {
        return e164;
    }
}
//# sourceMappingURL=phone.js.map